import { useState, useEffect } from "react";
import { Card, Form, Button, Spinner, Alert } from "react-bootstrap";
import APIFunction from "../util/api";
import { INFO_STATEMENTS, PENDING, SYSTEM_DEFAULTS } from "../util/constants";

export default function InitiatePayment({ show, onClose }){
    const [form, setForm] = useState({
        name: "",
        amount: "",
        category: "",
        savingPlanId: "",
    });
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null);
    const [savingsPlans, setSavingsPlans] = useState([]);
    const [loadingSavingsPlans, setLoadingSavingsPlans] = useState(false);

    useEffect(() => {
        if (show) {
          fetchSavingsPlans();
          clearFields();
        }
    }, [show]);

    const clearFields = () => {
        setForm({
            amount: "",
            savingPlanId: "",
            category: "",
            name: "",
        });
        setStatus(null)
    }

    const fetchSavingsPlans = async () => {
        setLoadingSavingsPlans(true);
        try {
          const response = await APIFunction.getUserSavingsPlan(1);
          if (response && response.data) {
            setSavingsPlans(response.data);
          }
        } catch (error) {
          console.error("Error fetching savings plans:", error);
          setSavingsPlans([]);
        } finally {
          setLoadingSavingsPlans(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatus("waiting");

        try {
            const payload = {
                amount: form.amount,
                status: PENDING,
                savingPlanId: form.savingPlanId,
                category: form.category,
                name: form.name,
            };

            const initiatePaymentResult = await APIFunction.createTransaction(payload);
            if (initiatePaymentResult.name === form.name) {
                setStatus(INFO_STATEMENTS.SUCCESS);
                setLoading(false);
                return;
            }
            setStatus(INFO_STATEMENTS.FAILED);
            setLoading(false);
            return;
        } catch (err) {
            console.error(err);
            setStatus(INFO_STATEMENTS.FAILED);
            setLoading(false);
        }
    }

    if (!show) return null;
    return (
        <div className="modal-backdrop">
            <Card className="transaction-modal-card">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <i className="bi bi-cash-stack me-2"></i>
                        Initiate Payment
                    </h5>
                    <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        onClick={onClose}
                        disabled={loading}
                    >
                        <i className="bi bi-x-lg"></i>
                    </Button>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                <i className="bi bi-input-cursor-text me-2"></i>
                                Transaction name
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                placeholder="Enter transaction name"
                                value={form.name}
                                onChange={handleChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-4">
                            <Form.Label>
                                <i className="bi bi-piggy-bank me-2"></i>
                                Savings Plan
                            </Form.Label>
                            {loadingSavingsPlans ? (
                                <div className="d-flex align-items-center">
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    <span>Loading savings plans...</span>
                                </div>
                            ) : (
                                <Form.Select
                                    name="savingPlanId"
                                    value={form.savingPlanId}
                                    onChange={handleChange}
                                    required
                                    >
                                    <option value="">Select a savings plan</option>
                                    {savingsPlans.map((plan) => (
                                        <option key={plan.id} value={plan.id}>
                                        {plan.name} - {plan.description || 'No description'}
                                        </option>
                                    ))}
                                </Form.Select>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                <i className="bi bi-currency-dollar me-2"></i>
                                Amount
                            </Form.Label>
                            <Form.Control
                                type="number"
                                name="amount"
                                placeholder="Enter amount"
                                value={form.amount}
                                onChange={handleChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                <i className="bi bi-tag me-2"></i>
                                Category
                            </Form.Label>
                            <Form.Select
                                name="category"
                                value={form.category}
                                onChange={handleChange}
                                >
                                <option value="">Select category</option>
                                    {SYSTEM_DEFAULTS.TRANSACTIONS.map((category) => (
                                    <option key={category} value={category}>
                                        {category}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        {status === "waiting" && (
                        <Alert variant="info" className="d-flex align-items-center">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Waiting for M-Pesa confirmation… please check your phone 📱
                        </Alert>
                        )}
                        
                        {status === INFO_STATEMENTS.SUCCESS && (
                        <Alert variant="success">
                            <i className="bi bi-check-circle me-2"></i>
                            Payment successful ✅
                        </Alert>
                        )}
                        
                        {status === INFO_STATEMENTS.FAILED && (
                        <Alert variant="danger">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            Payment failed ❌ Please try again
                        </Alert>
                        )}

                        <div className="d-flex gap-2">
                            <Button 
                                type="submit" 
                                variant="primary" 
                                disabled={loading}
                                className="flex-fill"
                            >
                                {loading ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Processing...
                                </>
                                ) : (
                                <>
                                    <i className="bi bi-cash-stack me-2"></i>
                                    Initiate payment
                                </>
                                )}
                            </Button>
                            
                            <Button 
                                variant="outline-secondary" 
                                onClick={onClose}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </div>
    )
}